import { useQuery } from "@tanstack/react-query";
import {
  fetchDispensaryPosTrends,
  formatStoreDateTime,
  type PosTransactionTrends,
} from "../lib/dispensary-trends-api";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

/**
 * Recent deliveries straight from the dispensary's POS.
 *
 * This is history, not a work queue. The reporting feed the warehouse is built
 * from carries no customer PII by design (see `docs/dutchie-api-spec.md`), so
 * there is no name, address, item list or live status to show — and nothing
 * here is actionable. Ingested Dutchie orders (`/Deliveries` → `sales_orders`)
 * are the ones that appear on the Drop tab with a customer and a status.
 *
 * Shares the trends query key, so opening this tab costs no extra request when
 * the Delivery Trends card has already loaded.
 */
const TRENDS_WINDOW_DAYS = 90;

export function PosDeliveriesList() {
  const { getToken, dispensaryId } = useDispensaryWorkspace();

  const { data: trends, isLoading } = useQuery<PosTransactionTrends | null>({
    queryKey: ["dispensary", "pos-trends", dispensaryId, TRENDS_WINDOW_DAYS],
    enabled: dispensaryId != null,
    queryFn: async () => fetchDispensaryPosTrends(getToken, dispensaryId!, TRENDS_WINDOW_DAYS),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <div className="divide-y divide-[#F5F5F3]">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex animate-pulse items-center justify-between px-5 py-4">
            <div className="h-3 w-40 rounded bg-[#F0F0F0]" />
            <div className="h-3 w-16 rounded bg-[#F0F0F0]" />
          </div>
        ))}
      </div>
    );
  }

  const rows = trends?.recentDeliveries ?? [];
  if (rows.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <p className="text-sm font-medium text-[#111111]">No POS deliveries yet</p>
        <p className="mt-2 text-sm text-[#666666]">
          Deliveries appear here once this location&rsquo;s POS transactions have synced.
        </p>
      </div>
    );
  }

  const timeZone = trends?.window.timeZone ?? "America/Denver";

  return (
    <>
      <ul className="divide-y divide-[#F5F5F3]">
        {rows.map((r) => (
          <li key={r.posTransactionId} className="flex items-center justify-between gap-3 px-5 py-3.5">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-[#111111]">
                {formatStoreDateTime(r.atIso, timeZone)}
              </div>
              <div className="mt-0.5 text-xs text-[#888888]">
                #{r.posTransactionId}
                {r.orderSource ? ` · ${r.orderSource}` : ""}
              </div>
            </div>
            <span className="flex-shrink-0 text-sm font-semibold text-[#111111]">
              ${(r.totalCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </span>
          </li>
        ))}
      </ul>
      <p className="border-t border-[#F0F0F0] bg-[#FAFAFA] px-5 py-3 text-xs text-[#888888]">
        Completed sales from the store&rsquo;s POS — no customer or address details are pulled from the
        reporting feed. Orders fulfilled through Drop appear on the Drop tab.
      </p>
    </>
  );
}
